import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Briefcase, Building2, Users, Clock, Shield, CreditCard, CheckCircle2, ArrowRight, FileText } from 'lucide-react';

const CORPORATE_HERO = "https://images.unsplash.com/photo-1560179707-f14e90ef3623?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const CorporatePage = () => {
  const services = [
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: "Executive Transfers",
      description: "Premium sedan and SUV services for executives, board members, and VIP clients. First-class service with professional, suited drivers."
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Office Account Services",
      description: "Streamlined corporate accounts with consolidated monthly billing, detailed reporting, and dedicated account management."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Staff Transportation",
      description: "Reliable transport for your team – from daily commutes to late-night shifts. Keep your employees safe and on time."
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Conference & Events",
      description: "Coordinated transport for conferences, seminars, and corporate events. We handle the logistics so you can focus on business."
    }
  ];

  const benefits = [
    "Dedicated account manager for personalized service",
    "Consolidated monthly invoicing for easy expense tracking",
    "Priority booking during peak periods",
    "Detailed trip reports for compliance and expense management",
    "Flexible payment options including corporate credit",
    "24/7 booking support via phone, email, or online portal",
    "GPS tracking and trip notifications",
    "Carbon offset options for environmentally conscious businesses"
  ];

  return (
    <div data-testid="corporate-page">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="pt-28 pb-20 relative"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(26, 26, 26, 0.85), rgba(26, 26, 26, 0.7)), url(${CORPORATE_HERO})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl text-white">
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="w-5 h-5 text-primary" />
              <span className="text-xs font-bold uppercase tracking-widest text-primary">Corporate Services</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
              Corporate <span className="text-primary">Transport</span> Solutions
            </h1>
            <p className="mt-6 text-gray-300 text-lg leading-relaxed">
              Partner with Melbourne's trusted taxi service for all your corporate transportation needs. 
              From executive transfers to staff transport, we deliver reliability, professionalism, and 
              seamless billing solutions tailored to your business.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link 
                to="/contact"
                className="bg-primary hover:bg-primary-hover text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center gap-2"
              >
                Book Corporate Ride
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a 
                href="tel:1300096685"
                className="bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 border border-white/20"
              >
                Call 1300 096 685
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">What We Offer</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-2">
              Corporate Transport Services
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Comprehensive transport solutions designed for the modern business. We understand that time is money – 
              our services are built to maximize efficiency and minimize hassle.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="p-8 rounded-xl border border-gray-100 bg-white hover:border-primary hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-14 h-14 rounded-lg bg-orange-50 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                  {service.icon}
                </div>
                <h3 className="font-heading text-xl font-bold text-secondary mb-3">{service.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-primary">Account Benefits</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-2 mb-6">
                Why Businesses Choose 13cabsbook
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Over the years, we've built strong relationships with businesses across Melbourne – from startups to 
                ASX-listed companies. Our corporate accounts are designed to give your business the flexibility, 
                control, and service quality you deserve.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-secondary text-white p-8 rounded-2xl">
              <h3 className="font-heading text-2xl font-bold mb-6">Quick Facts</h3>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg bg-white/10 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">24/7</p>
                    <p className="text-gray-400 text-sm">Booking availability</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg bg-white/10 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">100%</p>
                    <p className="text-gray-400 text-sm">Accredited drivers</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg bg-white/10 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">30 Days</p>
                    <p className="text-gray-400 text-sm">Payment terms available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">Industries We Serve</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-2">
              Trusted Across Industries
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              "Legal Firms", "Finance & Banking", "Healthcare", "Technology", "Real Estate",
              "Hospitality", "Education", "Government", "Manufacturing", "Consulting"
            ].map((industry, index) => (
              <span 
                key={index}
                className="px-4 py-2 bg-gray-100 text-secondary rounded-full text-sm font-medium"
              >
                {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-black mb-4">
            Ready to Simplify Your Corporate Transport?
          </h2>
          <p className="text-black/80 mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your requirements and set up a corporate account. 
            Our team will work with you to create a tailored transport solution for your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact"
              className="bg-secondary hover:bg-secondary/90 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Contact Our Team
            </Link>
            <a 
              href="mailto:13cabsbook.com.au@gmail.com"
              className="bg-white hover:bg-gray-100 text-secondary px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CorporatePage;
